import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, KeyIcon, SignalIcon } from '@heroicons/react/24/solid';
import { devices } from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import SystemResourcesChart from './SystemResourcesChart';
import StorageWidget from './StorageWidget';

interface Device {
  id: string;
  device_name: string;
  location: string;
  device_type: string;
  is_online: boolean;
  last_seen: string;
  firmware_version?: string;
  ip_address?: string;
  created_at: string;
  venue_id?: string;
  venue_name?: string;
  disk_total_gb?: number;
  disk_used_gb?: number;
  disk_available_gb?: number;
  disk_usage_percent?: number;
  cpu_usage_percent?: number;
  memory_usage_percent?: number;
  temperature_celsius?: number;
  swap_usage_percent?: number;
  spl_logging_enabled?: boolean;
}

interface ApiKey {
  id: string;
  device_id: string;
  name: string;
  key_preview: string;
  created_at: string;
  last_used?: string;
  is_active: boolean;
}

const DeviceManagement: React.FC = () => {
  const { user } = useAuth();
  const [deviceList, setDeviceList] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [showGenerateKey, setShowGenerateKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [newDevice, setNewDevice] = useState({
    device_name: '',
    location: '',
    device_type: 'SPL_MONITOR',
    venue_id: ''
  });
  const [generatedKey, setGeneratedKey] = useState<string>('');

  useEffect(() => {
    loadDevices();
  }, []);

  useEffect(() => {
    if (selectedDevice) {
      loadApiKeys(selectedDevice.id);
    }
  }, [selectedDevice]);

  const loadDevices = async () => {
    try {
      const response = await devices.list();
      // Map the response to handle any naming inconsistencies
      const mappedDevices = response.data.map((d: any) => ({
        id: d.id,
        device_name: d.device_name,
        location: d.location,
        device_type: d.device_type,
        is_online: d.status === 'online' || d.is_online,
        last_seen: d.last_seen,
        firmware_version: d.firmware_version,
        ip_address: d.ip_address,
        created_at: d.created_at,
        venue_id: d.venue_id,
        venue_name: d.venue_name,
        disk_total_gb: d.disk_total_gb,
        disk_used_gb: d.disk_used_gb,
        disk_available_gb: d.disk_available_gb,
        disk_usage_percent: d.disk_usage_percent,
        cpu_usage_percent: d.cpu_usage_percent,
        memory_usage_percent: d.memory_usage_percent,
        temperature_celsius: d.temperature_celsius,
        swap_usage_percent: d.swap_usage_percent,
        spl_logging_enabled: d.spl_logging_enabled !== false // Default to true
      }));
      setDeviceList(mappedDevices);
      if (mappedDevices.length > 0 && !selectedDevice) {
        setSelectedDevice(mappedDevices[0]);
      }
    } catch (error) {
      console.error('Failed to load devices', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApiKeys = async (deviceId: string) => {
    try {
      const response = await devices.getApiKeys(deviceId);
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to load API keys', error);
    }
  };

  const handleAddDevice = async () => {
    try {
      setError('');
      
      // Convert to camelCase for API
      const deviceData = {
        deviceName: newDevice.device_name,
        deviceType: newDevice.device_type,
        location: newDevice.location
      };
      
      console.log('Creating device with data:', deviceData);
      
      const response = await devices.create(deviceData);
      await loadDevices();
      setNewDevice({ device_name: '', location: '', device_type: 'SPL_MONITOR', venue_id: '' });
      setShowAddDevice(false);
      setSelectedDevice(response.data);
    } catch (error: any) {
      console.error('Failed to add device', error);
      setError(error.response?.data?.error || 'Failed to add device');
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;
    
    try {
      await devices.delete(deviceId);
      await loadDevices();
      setSelectedDevice(null);
    } catch (error) {
      console.error('Failed to delete device', error);
    }
  };

  const handleGenerateApiKey = async () => {
    console.log('Generate API key clicked', selectedDevice);
    if (!selectedDevice) {
      console.error('No device selected');
      return;
    }
    
    try {
      console.log('Generating API key for device:', selectedDevice.id);
      const response = await devices.generateApiKey(selectedDevice.id, {
        name: `Key for ${selectedDevice.device_name}`
      });
      console.log('API key generated:', response.data);
      setGeneratedKey(response.data.key);
      await loadApiKeys(selectedDevice.id);
      setShowGenerateKey(true);
    } catch (error: any) {
      console.error('Failed to generate API key', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.error || 'Failed to generate API key');
    }
  };

  const handleRevokeApiKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;
    
    try {
      await devices.revokeApiKey(keyId);
      if (selectedDevice) {
        await loadApiKeys(selectedDevice.id);
      }
    } catch (error) {
      console.error('Failed to revoke API key', error);
    }
  };

  const handleToggleSplLogging = async () => {
    if (!selectedDevice) return;
    
    try {
      const newValue = !selectedDevice.spl_logging_enabled;
      await devices.updateSplLogging(selectedDevice.id, newValue);
      
      // Update local state
      setSelectedDevice({ ...selectedDevice, spl_logging_enabled: newValue });
      setDeviceList(deviceList.map(d => 
        d.id === selectedDevice.id ? { ...d, spl_logging_enabled: newValue } : d
      ));
    } catch (error) {
      console.error('Failed to update SPL logging setting', error);
      setError('Failed to update SPL logging setting');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading devices...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Device Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Manage your SPL monitoring devices and API keys</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold">Devices</h2>
              <button
                onClick={() => setShowAddDevice(true)}
                className="p-1 bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {deviceList.map(device => (
                <div
                  key={device.id}
                  onClick={() => setSelectedDevice(device)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    selectedDevice?.id === device.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium">{device.device_name}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{device.location}</div>
                      {device.venue_name && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{device.venue_name}</div>
                      )}
                      <div className="flex items-center mt-1">
                        <SignalIcon 
                          className={`h-4 w-4 mr-1 ${
                            device.is_online ? 'text-green-500' : 'text-gray-400'
                          }`}
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {device.is_online ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Device Details */}
        <div className="lg:col-span-2">
          {selectedDevice ? (
            <div className="space-y-6">
              {/* Device Info */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-semibold">Device Details</h2>
                  <button
                    onClick={() => handleDeleteDevice(selectedDevice.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
                
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedDevice.device_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedDevice.location}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedDevice.device_type}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedDevice.is_online 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedDevice.is_online ? 'Online' : 'Offline'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Seen</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {format(new Date(selectedDevice.last_seen), 'PPp')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {format(new Date(selectedDevice.created_at), 'PP')}
                    </dd>
                  </div>
                  {selectedDevice.firmware_version && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Firmware</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedDevice.firmware_version}</dd>
                    </div>
                  )}
                  {selectedDevice.ip_address && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">IP Address</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedDevice.ip_address}</dd>
                    </div>
                  )}
                </dl>

                {/* SPL Logging Settings */}
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">SPL Data Logging</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Enable or disable sound pressure level data logging to the cloud
                      </p>
                    </div>
                    <button
                      onClick={handleToggleSplLogging}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        selectedDevice.spl_logging_enabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          selectedDevice.spl_logging_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    Status: <span className="font-medium">
                      {selectedDevice.spl_logging_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {!selectedDevice.spl_logging_enabled && (
                      <span className="text-yellow-600 ml-2">
                        (Compliance monitoring is paused)
                      </span>
                    )}
                  </div>
                </div>

                {/* System Resources */}
                <div className="mt-6">
                  <SystemResourcesChart
                    deviceId={selectedDevice.id}
                    refreshInterval={10000}
                    minutes={10}
                  />
                </div>

                {/* Recording Storage */}
                <div className="mt-6">
                  <StorageWidget
                    deviceId={selectedDevice.id}
                    type="device"
                    refreshInterval={30000}
                  />
                </div>

                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Setup Instructions</h3>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded p-3">
                    <code className="text-xs">
                      export API_KEY="[Generate key below]"<br />
                      export DEVICE_ID="{selectedDevice.id}"<br />
                      ./cloud-forwarder.sh
                    </code>
                  </div>
                </div>
              </div>

              {/* API Keys */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">API Keys</h2>
                  <button
                    onClick={handleGenerateApiKey}
                    className="flex items-center px-3 py-1 bg-blue-600 dark:bg-blue-700 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600"
                  >
                    <KeyIcon className="h-4 w-4 mr-1" />
                    Generate Key
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {apiKeys.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No API keys generated yet</p>
                ) : (
                  <div className="space-y-3">
                    {apiKeys.map(key => (
                      <div key={key.id} className="border rounded p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-sm">{key.name}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              Key: {key.key_preview}...
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Created: {format(new Date(key.created_at), 'PP')}
                            </div>
                            {key.last_used && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Last used: {format(new Date(key.last_used), 'PPp')}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRevokeApiKey(key.id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <p className="text-gray-500 dark:text-gray-400">Select a device to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Device Modal */}
      {showAddDevice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Add New Device</h3>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Device Name</label>
                <input
                  type="text"
                  value={newDevice.device_name}
                  onChange={(e) => setNewDevice({ ...newDevice, device_name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Main Room Monitor"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  value={newDevice.location}
                  onChange={(e) => setNewDevice({ ...newDevice, location: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Stage Left"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Device Type</label>
                <select
                  value={newDevice.device_type}
                  onChange={(e) => setNewDevice({ ...newDevice, device_type: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="SPL_MONITOR">SPL Monitor</option>
                  <option value="AUDIO_PROCESSOR">Audio Processor</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowAddDevice(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 hover:bg-gray-50 dark:bg-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDevice}
                className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
              >
                Add Device
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Key Modal */}
      {showGenerateKey && generatedKey && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold mb-4">API Key Generated</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
              <p className="text-sm text-yellow-800 mb-2">
                Save this key now! It won't be shown again.
              </p>
              <div className="bg-white dark:bg-gray-800 rounded p-2 font-mono text-sm break-all">
                {generatedKey}
              </div>
            </div>
            <button
              onClick={() => {
                setShowGenerateKey(false);
                setGeneratedKey('');
              }}
              className="w-full px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
            >
              I've Saved the Key
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManagement;